import { useEffect } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useForm } from "react-hook-form"
import { z } from "zod"

import {
  ConnectionType,
  SubmitZoneConnectionRate,
  ZoneConnectionRateProp,
} from "@/types/zone-connection-rates-type"
import { getConnectionType } from "@/lib/api/billing"
import { getAllZones } from "@/lib/api/zone"
import {
  addZoneConnectionRate,
  editZoneConnectionRate,
} from "@/lib/api/zone-connection-rates"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"

import { ComboboxSearch } from "../combobox-search"
import { Icons } from "../icons"
import { Card } from "../ui/card"
import { Input } from "../ui/input"

interface zoneData {
  zone_id: number
  zone_name: string
  client: string
}
const formSchema = z.object({
  zone_id: z.number().min(1, "Please select a zone"),
  connection_type_id: z.number().min(1, "Please select a connection type"),
  price_per_cubic: z.number().min(1, "Please enter a price per cubic "),
  environmental_charges: z
    .number()
    .min(1, "Please enter an environmental charges"),
  maintenance_service_charge: z
    .number()
    .min(1, "Please enter a main a maintenance service charge"),
  goverment_charge_percentage: z
    .number()
    .min(1, "Please enter a main a goverment charge percentage"),
})
export default function ZoneConnectionRatesForm({
  setShowZoneConnectionRatesModal,
  editData,
}: {
  setShowZoneConnectionRatesModal: React.Dispatch<React.SetStateAction<boolean>>
  editData?: ZoneConnectionRateProp
}) {
  const isConnectionTypeDropdown = false
  const data = useQueries({
    queries: [
      {
        queryKey: ["connectionTypesDetails"],
        queryFn: () => getConnectionType(isConnectionTypeDropdown),
      },
      {
        queryKey: ["zones"],
        queryFn: getAllZones,
      },
    ],
  })

  const connectionTypes: Array<{
    value: string
    label: string
    id?: string | number
  }> =
    (data[0]?.data as ConnectionType[]) // Ensure data is an array
      ?.map((item) => ({
        value: item.type_name,
        label: item.type_name,
        id: item.id,
      }))
      .filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.value === item.value), // Ensure unique values
      ) ?? []

  const zones: Array<{
    value: string
    label: string
    id: number
  }> =
    (data[1]?.data?.results as zoneData[])
      ?.map((item) => ({
        value: item.zone_name,
        label: item.zone_name,
        id: item.zone_id,
      }))
      .filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.value === item.value),
      ) ?? []

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      zone_id: 0,
      connection_type_id: 0,
      price_per_cubic: 0,
      environmental_charges: 0,
      maintenance_service_charge: 0,
      goverment_charge_percentage: 0,
    },
  })

  useEffect(() => {
    if (editData) {
      form.reset({
        zone_id: editData?.zone_id,
        connection_type_id: editData?.connection_type_id,
        price_per_cubic: editData?.price_per_cubic,
        environmental_charges: editData?.environmental_charges_percentage,
        maintenance_service_charge: editData?.maintenance_service_charge,
        goverment_charge_percentage: editData?.govermental_charges_percentage,
      })
    }
  }, [editData, form])

  const queryClient = useQueryClient()
  const { toast } = useToast()
  const navigate = useNavigate()
  const mutation = useMutation({
    mutationFn: async (payload: SubmitZoneConnectionRate) => {
      if (editData?.id) {
        await editZoneConnectionRate(payload, editData.id)
      } else {
        await addZoneConnectionRate(payload)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connection-rates"] })
      toast({
        description: editData
          ? "Zone connection rate edited successfully"
          : "Zone connection rate added successfully",
      })
      setShowZoneConnectionRatesModal(false)
      navigate({ to: "/zone-settings/zone-connection-rates" })
    },
  })

  function onSubmit(values: z.infer<typeof formSchema>) {
    const payload: SubmitZoneConnectionRate = {
      zone_id: values.zone_id,
      connection_type_id: values.connection_type_id,
      price_per_cubic: values.price_per_cubic,
      environmental_charges: values.environmental_charges,
      maintenance_service_charge: values.maintenance_service_charge,
      goverment_charge_percentage: values.goverment_charge_percentage,
    }

    mutation.mutate(payload)
  }

  return (
    <div className="px-2">
      <DropdownMenuSeparator className="mt-4" />

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="grid grid-cols-1 gap-4 lg:container md:max-w-[1000px]"
        >
          <Card className="my-4 gap-4 p-6">
            <FormField
              control={form.control}
              name="zone_id"
              render={({ field }) => (
                <FormItem className="flex flex-col justify-center">
                  <label>
                    Zone <span className="text-red-600">*</span>
                  </label>
                  <ComboboxSearch
                    field={{
                      ...field,
                    }}
                    options={zones}
                    placeholder="Select zone"
                    idAsValue
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="connection_type_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <label>
                    Connection type <span className="text-red-600">*</span>
                  </label>
                  <ComboboxSearch
                    field={{
                      ...field,
                    }}
                    options={connectionTypes}
                    placeholder="Select connection type"
                    idAsValue
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="price_per_cubic"
              render={({ field }) => (
                <FormItem>
                  <label>
                    Price per cubic <span className="text-red-600">*</span>
                  </label>
                  <Input
                    type="number"
                    {...field}
                    onChange={(e) => {
                      const value = e.target.value
                      field.onChange(value === "" ? "" : Number(value))
                    }}
                  />

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="environmental_charges"
              render={({ field }) => (
                <FormItem>
                  <label>
                    Environmental charges
                    <span className="text-red-600">*</span>
                  </label>
                  <Input
                    type="number"
                    {...field}
                    onChange={(e) => {
                      const value = e.target.value
                      field.onChange(value === "" ? "" : Number(value))
                    }}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="maintenance_service_charge"
              render={({ field }) => (
                <FormItem>
                  <label>
                    Maintenance service charge
                    <span className="text-red-600">*</span>
                  </label>
                  <Input
                    type="number"
                    {...field}
                    onChange={(e) => {
                      const value = e.target.value
                      field.onChange(value === "" ? "" : Number(value))
                    }}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="goverment_charge_percentage"
              render={({ field }) => (
                <FormItem>
                  <label>
                    Goverment charge percentage
                    <span className="text-red-600">*</span>
                  </label>
                  <Input
                    type="number"
                    {...field}
                    onChange={(e) => {
                      const value = e.target.value
                      field.onChange(value === "" ? "" : Number(value))
                    }}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </Card>
          <div className="col-span-2 flex w-full justify-end gap-3">
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setShowZoneConnectionRatesModal(false)
              }}
              className="w-28"
            >
              Cancel
            </Button>
            {editData ? (
              <Button className="w-28" disabled={mutation.isPending}>
                {mutation.isPending && (
                  <Icons.loader className="mr-2 animate-spin" />
                )}
                Save Changes
              </Button>
            ) : (
              <Button className="w-28" disabled={mutation.isPending}>
                {mutation.isPending && (
                  <Icons.loader className="mr-2 animate-spin" />
                )}
                Add
              </Button>
            )}
          </div>
        </form>
      </Form>
    </div>
  )
}
